<template>
  <div
    class="flex grow flex-col overflow-y-auto border-r border-background-fg/8 bg-background pb-4 pt-5"
  >
    <div
      class="flex shrink-0 flex-col items-center justify-center px-4"
    >
      <IconBrandLogo class="h-auto w-full" />
      <p
        class="w-full text-right text-xs font-bold text-primary"
        >Group Web Portal</p
      >
    </div>
    <div class="w-full shrink-0 px-4">
      <p
        class="line-clamp-2 w-full text-lg font-medium text-background-fg/70"
      >
        {{ merchantGroup?.name }}
      </p>
    </div>
    <div
      class="mt-2 flex grow flex-col border-t border-background-fg/20 pt-4"
    >
      <nav
        class="flex flex-1 flex-col space-y-2 bg-background px-2"
        aria-label="Sidebar"
      >
        <template
          v-for="item in sidebarSections"
          :key="item.name"
        >
          <RouterLink
            #default="{
              href: childHref,
              navigate: childNavigate,
              route: { meta: childMeta },
              isExactActive: isChildActive,
            }"
            :to="item"
            custom
          >
            <div
              :class="[
                'space-y-1 border-l-2',
                isChildActive
                  ? 'border-primary bg-background-fg/3 text-background-fg/87'
                  : 'border-transparent bg-white text-background-fg/70',
              ]"
            >
              <a
                :href="childHref"
                :class="[
                  isChildActive
                    ? 'bg-primary/10'
                    : 'hover:bg-background-fg/3 hover:text-background-fg/87',
                  'group flex w-full items-center rounded-md p-2 text-sm font-medium text-background-fg/70',
                ]"
                @click="childNavigate"
              >
                <component
                  :is="childMeta.icon"
                  :class="['mr-3 size-6 shrink-0']"
                  aria-hidden="true"
                  @click="childNavigate"
                />
                {{ childMeta.pageTitle }}
              </a>
            </div>
          </RouterLink>
        </template>
        <div class="flex-1"> </div>

        <template v-if="merchantGroupAdmin?.displayEmail">
          <div class="h-px w-full bg-background-fg/20" />
          <div class="space-y-1">
            <div
              class="flex items-center px-4 py-2 text-sm font-medium text-background-fg/80"
            >
              {{ merchantGroupAdmin.displayEmail }}
            </div>
            <p
              v-if="merchantGroupAdmin.isStaffShadowUser"
              class="w-full px-4 text-xs font-bold text-primary"
              >Staff User</p
            >
            <RouterLink
              v-if="!merchantGroupAdmin.isStaffShadowUser"
              #default="{ navigate, href }"
              custom
              :to="{ name: 'changePasswordMerchantGroup' }"
            >
              <div class="relative">
                <a
                  class="block w-full px-4 py-2 text-left text-base font-medium text-background-fg/50 hover:bg-background-fg/10 hover:text-background-fg/80"
                  :href="href"
                  @click="navigate"
                >
                  {{ changePasswordText }}
                </a>
                <template v-if="shouldAlertPassword">
                  <IconExclamationCircleFilled
                    class="absolute right-1 top-0 size-4 text-error"
                  />
                </template>
              </div>
            </RouterLink>
            <button
              class="block w-full px-4 py-2 text-left text-base font-medium text-background-fg/50 hover:bg-background-fg/10 hover:text-background-fg/80"
              @click="onClickLogout"
            >
              Log Out
            </button>
          </div>
        </template>
      </nav>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMerchantGroupAdmin } from "@/composables/merchantGroup/useMerchantGroupAdmin"
import { queryClient } from "@/queryClient"
import {
  RouterLink,
  type RouteLocationAsRelative,
} from "vue-router"

type SidebarSection = RouteLocationAsRelative & {
  children?: Array<RouteLocationAsRelative>
}

const merchantGroupAdmin = useMerchantGroupAdmin()
const merchantGroup = useMerchantGroup()

const sidebarSections = computed<Array<SidebarSection>>(
  () => [
    {
      name: "merchantGroupDashboard",
    },
    {
      name: "merchantGroupTransactionsReport",
    },
    {
      name: "merchantGroupUsersIndex",
    },
    {
      name: "merchantGroupTerminalsIndex",
    },
  ]
)

const { setToken } = useMerchantGroupAuth()

const onClickLogout = () => {
  setToken(null)
  queryClient.invalidateQueries()
}

const shouldAlertPassword = computed(
  () => merchantGroupAdmin.value?.hasPassword === false
)
const changePasswordText = computed(() =>
  merchantGroupAdmin.value?.hasPassword
    ? "Change Password"
    : "Set Password"
)
</script>
